import { Logger, LOG_LEVEL } from '@krakenjs/beaver-logger';
import { CONTEXT_ROOT } from './constants';

const LOG_PREFIX = 'developerstudionodeweb::';

// https://github.com/krakenjs/@krakenjs/beaver-logger#configuration
// this method returns a BeaverLogger with the url configuration field built using the country prm
const getBeaverLogger = () =>
  new Logger({
    // URL to post logs to
    url: `${CONTEXT_ROOT}/api/log`,

    // Prefix for each log
    prefix: LOG_PREFIX,

    // Interval at which to automatically flush logs to the server
    flushInterval: process.env.NODE_ENV === 'development' ? 1000 : 1000 * 60,

    // Log level to display in the browser console
    logLevel: LOG_LEVEL.INFO
  });

// Convert message to an object if not already
const normalizeMessage = (message) => {
  let payload = message;

  if (typeof message !== 'object') {
    payload = {
      message
    };
  } else if (message instanceof Error) {
    payload = {
      message: String(message)
    };
  }

  return payload;
};

// Normalize message
const normalizeLog =
  (logFn) =>
  (...args) => {
    if (!args.length) {
      // eslint-disable-next-line no-console
      return console.warn('No arguments were passed into logger');
    }
    if (args.length === 1) {
      return logFn(args[0]);
    }

    return logFn(args[0], normalizeMessage(args[1]));
  };

export function attachUnloadListeners(clientLogger) {
  // Flush logs before unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      clientLogger.cal.info('window_beforeunload');
      clientLogger.immediateFlush();
    });
  }
}

// Attach @krakenjs/beaver-logger functions to clientLogger
export function applyBeaverLogger(clientLogger, logger) {
  // Log to CAL / Splunk
  // ie. clientLogger.cal.error('SERVICE_ERROR', { message: 'My error' }))
  // https://github.com/krakenjs/@krakenjs/beaver-logger#basic-logging
  clientLogger.cal = {};
  ['info', 'debug', 'warn', 'error'].forEach((call) => {
    clientLogger.cal[call] = normalizeLog(logger[call]);
  });

  // Log only to Splunk
  clientLogger.splunk = {};
  ['info', 'error'].forEach((call) => {
    clientLogger.splunk[call] = normalizeLog(logger[call]);
  });

  // Log only to FPTI
  clientLogger.fpti = (message) => {
    logger.track(message);
  };

  // Flush logs manually (@krakenjs/beaver-logger flushes automatically in intervals)
  clientLogger.flush = logger.flush;

  // Flush logs immediately without debouncing
  clientLogger.immediateFlush = logger.immediateFlush;

  attachUnloadListeners(clientLogger);
}

// use this method to apply a custom BeaverLogger to the logger;
// the BeaverLogger is built using the specific country
export function applyCustomBeaverLogger(clientLogger) {
  const customBeaverLogger = getBeaverLogger();
  applyBeaverLogger(clientLogger, customBeaverLogger);
}

function applyInitialBeaverLogger() {
  const clientLogger = {};
  applyCustomBeaverLogger(clientLogger);
  return clientLogger;
}

const clientLogger = applyInitialBeaverLogger();

export default clientLogger;
